/* eslint-disable no-console */
import {
  Divider, IconButton, IconButtonType, Spinner
} from "@whitespace/whitespace-ui";
import {
  useState, useCallback, useEffect, useMemo
} from "react";
import {
  Card, Col, Container, Row, ToggleButton, ToggleButtonGroup
} from "react-bootstrap";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { mapUserResponseItemToUser } from "mappers/mapUserResponseItemToUser/mapUserResponseItemToUser";
import { mapGroupResponseItemToGroup } from "mappers/mapGroupResponseItemToGroup/mapGroupResponseItemToGroup";
import { IUser } from "interfaces/models/IUser";
import { UserType } from "interfaces/models/UserType";
import { UserQuickFilter } from "interfaces/models/UserQuickFilter";
import { UserStatusType } from "interfaces/models/UserStatusType";
import IUserResponseItem from "interfaces/responses/IUserResponseItem";
import { UserQuickFilterDisplay } from "interfaces/models/UserQuickFilterDisplay";
import { useNavigate } from "react-router-dom";
import SearchEntry from "components/SearchEntry/SearchEntry";
import { FunnelFill } from "react-bootstrap-icons";
import UserFilterDrawer from "domains/Home/Users/UserFilterDrawer/UserFilterDrawer";
import { useStateMachine } from "little-state-machine";
import { updateIsUserFilterDrawerOpen } from "actions/updateIsUserFilterDrawerOpen";
import { updateUserFilter } from "actions/updateUserFilter";
import { updateGroupsFiltered } from "actions/updateGroupsFilteredUsers";
import { updateUserTypes } from "actions/updateUserTypes";
import { makeGridAACompliant } from "modules/SyncfusionAAFixer";
import AmplifyError from "components/AmplifyError/AmplifyError";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Sort,
  Page as Paging,
  PageSettingsModel,
  Filter,
  Resize,
  FilterSettingsModel
} from "@syncfusion/ej2-react-grids";
import "./Users.scss";
import IGroup from "interfaces/models/IGroup";
import IGroupResponseItem from "interfaces/responses/IGroupResponseItem";
import { parseUsingParseInt } from "helpers/stringUtils";
import { IUserStatusViewModel } from "interfaces/models/IUserStatusViewModel";
import UsernameTemplate from "./Templates/UsernameTemplate";
import EmailTemplate from "./Templates/EmailTemplate";
import GroupsSitesTemplate from "./Templates/GroupsSitesTemplate";
import StatusTemplate from "./Templates/StatusTemplate";
import ManageHistoryTemplate from "./Templates/ManageHistoryTemplate";
import "styles/sf-datagrid.scss";
import { mapUserStatusesToViewModels } from "./DataMappers/UserStatusesMapper";

const pageSize: number = parseUsingParseInt(process.env.REACT_APP_PAGE_SIZE);
const pageSettings: PageSettingsModel = {
  pageSize,
  pageSizes: [5, 10, 20, 50, "All"]
};
const filterSettings: FilterSettingsModel = { type: "Excel" };
let grid: GridComponent | undefined;

const Users = () => {
  const [users, setUsers] = useState<IUser[] | undefined>(undefined);
  const [group, setGroup] = useState<string | undefined>(undefined);
  const [filteredUsers, setFilteredUsers] = useState<IUserStatusViewModel[] | undefined>(undefined);
  const [renderingComplete, setRenderingComplete] = useState(false);
  const navigate = useNavigate();
  const { state } = useStateMachine();

  const { actions } = useStateMachine({
    updateIsUserFilterDrawerOpen,
    updateUserFilter,
    updateGroupsFiltered,
    updateUserTypes
  });

  const {
    isSending: getUserIsSending, errors: getUserErrors, data: getUserData, sendRequest: getUserSendRequest, statusCode: getUserStatusCode
  } = useAmplifyREST();

  const {
    isSending: getGroupsIsSending, errors: getGroupsErrors, data: getGroupsData, sendRequest: getGroupsSendRequest
  } = useAmplifyREST();

  const isSending = getUserIsSending || getGroupsIsSending;
  const errors = getUserErrors || getGroupsErrors;

  const filterUsers = () => {
    if (users) {
      let usersFiltered = users;

      if (state.userFilter!.quickFilter !== UserQuickFilter.All) {
        if (state.userFilter!.quickFilter === UserQuickFilter.Active) {
          usersFiltered = usersFiltered.filter(user => user.status === UserStatusType.Active);
        } else if (state.userFilter!.quickFilter === UserQuickFilter.Inactive) {
          usersFiltered = usersFiltered.filter(user => user.status === UserStatusType.Inactive);
        } else if (state.userFilter!.quickFilter === UserQuickFilter.RequiresAction) {
          usersFiltered = usersFiltered.filter(user => user.requests! === true);
        }
      }

      if (state.userFilter!.searchText) {
        const searchTerm = new RegExp(state.userFilter!.searchText, "gi");
        usersFiltered = usersFiltered.filter(user => user.emailAddress.match(searchTerm) || user.username.match(searchTerm));
      }

      if (![0, 3].includes(state.userFilter.userTypes.length)) {
        usersFiltered = usersFiltered.filter(user => (
          (
            (state.userFilter.userTypes.includes(UserType.Local) && !user.isFederatedUser)
            || (state.userFilter.userTypes.includes(UserType.Federated) && user.isFederatedUser)
            || (state.userFilter.userTypes.includes(UserType.SSOPortalAdmin) && user.isPortalAdmin))
        ));
      }

      setFilteredUsers(mapUserStatusesToViewModels(usersFiltered));

      const userTypesFiltered: UserType[] = [];

      if (users.some(user => user.isFederatedUser)) {
        userTypesFiltered.push(UserType.Federated);
      }
      if (users.some(user => !user.isFederatedUser)) {
        userTypesFiltered.push(UserType.Local);
      }

      if (users.some(user => user.isPortalAdmin)) {
        userTypesFiltered.push(UserType.SSOPortalAdmin);
      }

      actions.updateUserTypes(userTypesFiltered);
    }

    if (state.userFilter?.group && getGroupsData) {
      const selectedGroupId = state.userFilter.group;

      if (group !== selectedGroupId && getGroupsData.find((g : IGroupResponseItem) => g.id === selectedGroupId)) {
        setGroup(selectedGroupId);
      }
    }
  };

  const selectedGroupName = useMemo(() => {
    if (group && getGroupsData) {
      return getGroupsData.find((g : IGroupResponseItem) => g.id === group)?.name;
    }

    return "";
  }, [group, getGroupsData]);

  useEffect(() => {
    if (state.userFilter) {
      filterUsers();
    }
  }, [state.userFilter]);

  const onCloseMenu = useCallback(() => {
    actions.updateIsUserFilterDrawerOpen(false);
  }, [actions]);

  const handleQuickFilterChange = (e: UserQuickFilter) => {
    if (state.userFilter) {
      const userFilter = { ...state.userFilter };
      userFilter!.quickFilter = e;
      actions.updateUserFilter(userFilter);
    }
  };

  const handleTextSearch = (searchText: string) => {
    if (state.userFilter) {
      const userFilter = { ...state.userFilter };
      userFilter!.searchText = searchText;
      actions.updateUserFilter(userFilter);
    }
  };

  useEffect(() => {
    filterUsers();
  }, [users]);

  useEffect(() => {
    if (getUserData && getGroupsData) {
      const userList = getUserData.map((userResponse : IUserResponseItem) => mapUserResponseItemToUser(userResponse, getGroupsData)) as IUser[];
      setUsers(userList);
      setFilteredUsers(mapUserStatusesToViewModels(userList));
    }
  }, [getUserData, getGroupsData]);

  useEffect(() => {
    if (group) {
      setUsers([]);
      setFilteredUsers([]);
      getUserSendRequest("users", "GET", { group });
    }
  }, [group]);

  useEffect(() => {
    if (getGroupsData) {
      const groupList = getGroupsData.map((groupResponse : IGroupResponseItem) => mapGroupResponseItemToGroup(groupResponse)) as IGroup[];
      actions.updateGroupsFiltered(groupList);

      if (!getGroupsData.find((g : IGroupResponseItem) => g.id === state.userFilter?.group)) {
        const userFilter = { ...state.userFilter };
        userFilter!.group = undefined;
        state.userFilter.group = undefined;
        actions.updateUserFilter(userFilter);
        if (groupList.length > 1) {
          actions.updateIsUserFilterDrawerOpen(true);
        }
      }

      if (!state.userFilter?.group) {
        if (groupList.length === 1 && state.userFilter) {
          const userFilter = { ...state.userFilter };
          userFilter!.group = groupList[0].groupId;
          actions.updateUserFilter(userFilter);
        } else {
          actions.updateIsUserFilterDrawerOpen(true);
        }
      } else {
        filterUsers();
      }
    }
  }, [getGroupsData]);

  useEffect(() => {
    getGroupsSendRequest("groups", "GET");
  }, [getGroupsSendRequest]);

  useEffect(() => {
    if (![0, 200].includes(getUserStatusCode)) {
      setRenderingComplete(true);
    }
  }, [getUserStatusCode]);

  const usernameTemplateInstance = UsernameTemplate;
  const emailTemplateInstance = EmailTemplate;
  const groupsSitesTemplateInstance = GroupsSitesTemplate;
  const statusTemplateInstance = StatusTemplate;
  const manageHistoryTemplateInstance = ManageHistoryTemplate;

  return (
    <>
      <UserFilterDrawer isOpen={state.isUserFilterDrawerOpen} onClose={onCloseMenu} />
      {errors && <AmplifyError errors={errors} />}
      <Card>
        <Card.Header>
          <Row className="w-100">
            <Col xs={8}>
              <h1>
                User Management
                {(selectedGroupName) && (
                  <>
                    {" "}
                    (
                    {selectedGroupName}
                    )
                  </>
                )}
                <Spinner animation="grow" visible={isSending || !renderingComplete} spinnerText="Loading" variant="info" />
              </h1>
            </Col>
            <Col xs={4} className="d-flex justify-content-end">
              <IconButton
                data-testid="btnCreateUser"
                iconType={IconButtonType.Add}
                className="btn-create-user"
                variant="outline-primary"
                onClick={() => navigate("/createuser")}
              >
                New User
              </IconButton>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="no-padding">
          <Row className="w-100 align-items-center top-margin-small bottom-margin-small">
            <Col xs={12} md={7} className="d-flex">
              <ToggleButtonGroup
                type="radio"
                name="filter"
                className="quick-filter-wrapper"
                onChange={handleQuickFilterChange}
                defaultValue={state.userFilter.quickFilter}
              >
                <ToggleButton
                  id="users_all"
                  data-testid="users_all"
                  variant="outline-primary"
                  value={UserQuickFilter.All}
                >
                  {UserQuickFilterDisplay[UserQuickFilter.All]}
                </ToggleButton>
                <ToggleButton
                  id="users_active"
                  data-testid="users_active"
                  variant="outline-primary"
                  value={UserQuickFilter.Active}
                >
                  Active
                </ToggleButton>
                <ToggleButton
                  id="users_requires_action"
                  data-testid="users_requires_action"
                  variant="outline-primary"
                  value={UserQuickFilter.RequiresAction}
                >
                  Requires Action
                </ToggleButton>
                <ToggleButton
                  id="users_inactive"
                  data-testid="users_inactive"
                  variant="outline-primary"
                  value={UserQuickFilter.Inactive}
                >
                  Inactive

                </ToggleButton>
              </ToggleButtonGroup>
            </Col>
            <Col xs={12} md={5} className="d-flex justify-content-end">
              <div className="d-flex user-filter">
                <SearchEntry defaultValue={state.userFilter!.searchText!} onSearch={handleTextSearch} />
                <button
                  data-testid="btnUserFilter"
                  className="btn btn-filter"
                  name="filterButton"
                  type="button"
                  aria-label="Open User Filter"
                  onClick={() => { actions.updateIsUserFilterDrawerOpen(true); }}
                >
                  <FunnelFill size={30} data-testid="filterButton" />
                </button>
              </div>
            </Col>
          </Row>
          <Divider />
          <section className="pt-2">
            <Container fluid>
              <Row>
                <Col>
                  <div className="control-pane">
                    <div className="control-section">
                      <GridComponent
                        ref={(g: GridComponent) => {
                          grid = g;
                        }}
                        id="overviewgrid"
                        data-testid="userStatuses"
                        allowPaging
                        allowResizing
                        pageSettings={pageSettings}
                        allowSorting
                        allowFiltering
                        filterSettings={filterSettings}
                        allowSelection={false}
                        dataSource={filteredUsers ?? []}
                        actionComplete={() => {
                          makeGridAACompliant(grid!, () => {
                            setRenderingComplete(true);
                          });
                        }}
                        loadingIndicator={{ indicatorType: "Shimmer" }}
                        type=""
                      >
                        <ColumnsDirective>
                          <ColumnDirective
                            field="id"
                            headerText="id"
                            visible={false}
                            isPrimaryKey
                          />
                          <ColumnDirective
                            field="username"
                            headerText="User Name"
                            template={usernameTemplateInstance}
                          />
                          <ColumnDirective
                            field="emailAddress"
                            headerText="Email Address"
                            template={emailTemplateInstance}
                            width={300}
                          />
                          <ColumnDirective
                            field="applicationCount"
                            headerText="Group/Sites"
                            template={groupsSitesTemplateInstance}
                            width={100}
                          />
                          <ColumnDirective
                            field="status"
                            headerText="Status"
                            template={statusTemplateInstance}
                            width={100}
                          />
                          <ColumnDirective
                            field="userCount"
                            headerText=""
                            template={manageHistoryTemplateInstance}
                            allowFiltering={false}
                            allowSorting={false}
                            width={100}
                          />
                        </ColumnsDirective>
                        <Inject services={[Filter, Paging, Sort, Resize]} />
                      </GridComponent>
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          </section>
        </Card.Body>
      </Card>
    </>
  );
};

export default Users;
